import { assertIsNotUndefined } from "common/utils/assertOfType";
import uid from "common/utils/uid";
import type { DocBuildingGraphFilterGraph, DocBuildingGraphFilterNode } from "../walls2/wallPlaneFilters";

export function makeEmptyFaceNodeFilterGraph(): {
  readonly filterGraphId: string;
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
} {
  const filterGraphId = `graph_${uid()}`;
  const rootNodeId = `node_${uid()}`;
  return {
    filterGraphId,
    filterGraphs: {
      [filterGraphId]: {
        rootNodeId,
        nodes: {
          [rootNodeId]: {
            filterType: "AND",
            targetType: "FACE_NODE",
            childrenNodeIds: [],
          },
        },
      },
    },
  };
}

export function instanceGraphHasPredicates(
  filterGraphs: Record<string, DocBuildingGraphFilterGraph> | undefined,
  filterGraphId: string | undefined,
): boolean {
  if (!filterGraphs || !filterGraphId) {
    return false;
  }
  const graph = filterGraphs[filterGraphId];
  if (!graph) {
    return false;
  }
  return graphContainsPredicate(graph, graph.rootNodeId);
}

function graphContainsPredicate(graph: DocBuildingGraphFilterGraph, nodeId: string): boolean {
  const node = graph.nodes[nodeId];
  if (!node) {
    return false;
  }
  switch (node.filterType) {
    case "PREDICATE":
      return true;
    case "NOT":
      return graphContainsPredicate(graph, node.childNodeId);
    case "AND":
    case "OR":
      return node.childrenNodeIds.some((childId) => graphContainsPredicate(graph, childId));
    default:
      return false;
  }
}

/**
 * AND-composes two FACE_NODE filter graphs into a new graph bundle.
 * Either side may be undefined (returns the other in a new bundle wrapper).
 */
export function composeFilterGraphsAnd(
  left: DocBuildingGraphFilterGraph | undefined,
  right: DocBuildingGraphFilterGraph | undefined,
): {
  readonly filterGraphId: string;
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
} {
  if (!left && !right) {
    return makeEmptyFaceNodeFilterGraph();
  }
  if (!left) {
    assertIsNotUndefined(right);
    return wrapFilterGraphBundle(right);
  }
  if (!right) {
    return wrapFilterGraphBundle(left);
  }

  const filterGraphId = `graph_${uid()}`;
  const rootNodeId = `node_${uid()}`;
  const mergedNodes: Record<string, DocBuildingGraphFilterNode> = {
    ...left.nodes,
    ...right.nodes,
  };
  const leftRootId = materializeRoot(left, mergedNodes);
  const rightRootId = materializeRoot(right, mergedNodes);
  mergedNodes[rootNodeId] = {
    filterType: "AND",
    targetType: "FACE_NODE",
    childrenNodeIds: [leftRootId, rightRootId],
  };
  return {
    filterGraphId,
    filterGraphs: {
      [filterGraphId]: {
        rootNodeId,
        nodes: mergedNodes,
      },
    },
  };
}

function wrapFilterGraphBundle(graph: DocBuildingGraphFilterGraph): {
  readonly filterGraphId: string;
  readonly filterGraphs: Record<string, DocBuildingGraphFilterGraph>;
} {
  const filterGraphId = `graph_${uid()}`;
  return {
    filterGraphId,
    filterGraphs: {
      [filterGraphId]: graph,
    },
  };
}

function materializeRoot(
  graph: DocBuildingGraphFilterGraph,
  targetNodes: Record<string, DocBuildingGraphFilterNode>,
): string {
  for (const [nodeId, node] of Object.entries(graph.nodes)) {
    targetNodes[nodeId] = node;
  }
  return graph.rootNodeId;
}
